import { Injectable } from '@angular/core'
import { ApiService } from './api.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { use, unwrapApiResult } from './utils'

/** Category rescan cooldown: 5 days. */
const CATEGORY_TTL_MS = 86400000 * 5
/** Product resync cooldown: 24h. */
const PRODUCT_TTL_MS = 86400000
/** Default delivery period (in days) assumed for FNAC products. */
const DELIVERY_PERIOD_DAYS = 15

/** Matches a FNAC seller code: an optional leading non-digit letter followed by 4-15 digits. */
const SELLER_CODE_RE = /^(\D?)(\d{4,15})/

@Injectable({ providedIn: 'root' })
export class FnacService {

  private _fss: FirestoreService2
  private _api: ApiService
  /** In-flight syncs by sellerCode, to avoid duplicate HTTP+writes when multiple callers race. */
  private inflight = new Map<string, Promise<any>>()

  constructor(
    public g: GlobalService,
  ) { }

  private get fss(): FirestoreService2 {
    if (!this._fss) this._fss = use<FirestoreService2>(FirestoreService2)
    return this._fss
  }

  private get api(): ApiService {
    if (!this._api) this._api = use<ApiService>(ApiService)
    return this._api
  }

  private isFnacSeller(seller: any): boolean {
    return seller === 'FNAC' || seller === 'ODISSEIAS'
  }

  /** ODISSEIAS products are identified by a `seller` starting with `odiss`. */
  private isOdisseiasSeller(seller: any): boolean {
    return String(seller || '').toLowerCase().startsWith('odiss')
  }

  /**
   * Extracts the numeric FNAC product id from a seller code.
   * Returns null when the code has no 4-15 digit group.
   */
  private extractProductId(sellerCode: any): string | null {
    if (!sellerCode) return null
    const match = String(sellerCode).match(SELLER_CODE_RE)
    return match && match[2] ? match[2] : null
  }

  /** Points for a FNAC product: ceil(price / cbXrate), with safe fallbacks. */
  private calcPoints(price: any): number {
    const p = parseFloat(price)
    const cbXrate = this.g.program?.cbXrate || 1
    if (!isFinite(p) || cbXrate <= 0) return 0
    return Math.ceil(p / cbXrate)
  }

  /**
   * Builds the payload shared by the create and update branches of `updateProduct`.
   * `updatedBy`/`updatedAt` are intentionally not set here: `FirestoreService2.prepareData`
   * overrides them on every write.
   */
  private stampProduct(product: any, fnacProd: any, now: number): any {
    // FNAC API fields (price, stock, name, image) must win over the local stub,
    // otherwise the cached price/stock would be persisted back to `products`
    // and the refresh would be a no-op. Mirrors the original behaviour in
    // library-spec.xml:7428 (`{ ...product, ...fnacProd }`).
    const prod = { ...product, ...fnacProd }
    prod['updatedTimestamp'] = now
    prod['deliveryPeriod'] = DELIVERY_PERIOD_DAYS
    prod['points'] = this.calcPoints(prod['price'])
    prod['active'] = !!(prod.stock && +prod.price > 0)
    prod['fnac'] = true
    // ODISSEIAS products are VAT-exempt. The FNAC payload cannot signal this
    // (it always reports `MainOffer.Seller` as `Fnac.pt`), so the platform
    // `seller` decides.
    if (this.isOdisseiasSeller(prod['seller'])) prod['vatRate'] = 0
    return prod
  }

  /**
   * Resolves the product's FNAC seller code.
   *
   * By convention the `products` document id IS the seller code (`scanCategory`
   * writes `id: prod.sellerCode`, and every write here targets
   * `products/{sellerCode}`), so a document that is missing the explicit
   * `sellerCode` field can still be synced using its id. Without this fallback
   * such a product silently skipped the API call altogether.
   */
  private resolveSellerCode(product: any): string | null {
    const code = product?.sellerCode || product?.id
    return code ? String(code) : null
  }

  /**
   * Syncs a single product with the FNAC API when it is FNAC/ODISSEIAS.
   *
   * By default the 24h TTL (`PRODUCT_TTL_MS`) is respected, so catalogue
   * browsing doesn't hit the API for every product on every page view. Pass
   * `force = true` to bypass the TTL: the cart/checkout does this because that
   * is the moment the user is about to be charged, so price and stock must be
   * verified against FNAC on every visit, not read from a cached document.
   *
   * Resolves with the (possibly updated) product, and never rejects: on any
   * failure the original product is returned so callers like
   * `batchProductUpdate` cannot hang.
   */
  async updateProduct(product: any = null, force = false): Promise<any> {
    if (!product) return product
    if (!this.isFnacSeller(product.seller)) return product

    const now = Date.now()
    if (!force && product.updatedTimestamp && (now - product.updatedTimestamp) <= PRODUCT_TTL_MS) return product

    const sellerCode = this.resolveSellerCode(product)
    const pid = this.extractProductId(sellerCode)
    if (!pid) return product

    // Deduplicate concurrent syncs of the same product. A TTL short-circuit
    // never reaches this point, so an in-flight promise is always a real API
    // call and is safe to share with a forced caller.
    const existing = this.inflight.get(sellerCode)
    if (existing) return existing

    const p = this.doUpdateProduct(product, sellerCode, pid, now).finally(() => this.inflight.delete(sellerCode))
    this.inflight.set(sellerCode, p)
    return p
  }

  private async doUpdateProduct(product: any, sellerCode: string, pid: string, now: number): Promise<any> {
    try {
      const fnacProd = await this.fss.toPromise(this.api.get(`fnac/byId?productId=${encodeURIComponent(pid)}`)).then(unwrapApiResult)
      if (!fnacProd || typeof fnacProd.name !== 'string') return product

      // `sellerCode` (the id we just queried the API with) is the immutable
      // identity of the product. The product's name is not: FNAC can rename a
      // listing at any time (e.g. "Coluna Wi-Fi" -> "Coluna Wi-Fi Promocional")
      // without that meaning the sellerCode was reused for a different
      // product, so it must not be used to invalidate the sync.
      const prod = this.stampProduct(product, fnacProd, now)

      if (typeof product.name === 'string' && product.name) {
        // Existing product: refresh API-driven fields.
        await this.fss.update('products', sellerCode, prod, { silent: true })
      } else {
        // New/stub product (e.g. admin "add product" flow): merge so the write
        // doesn't fail if the document doesn't fully exist yet.
        await this.fss.merge('products', sellerCode, prod, { silent: true })
      }
      return prod
    } catch {
      return product
    }
  }

  /**
   * Refreshes FNAC/ODISSEIAS products in a list whose 24h TTL has elapsed.
   * Resolves with true when at least one product was actually updated, so the
   * caller can decide to reload. Never rejects: failed individual syncs are
   * swallowed and reported as "not updated".
   */
  async batchProductUpdate(products: any[]): Promise<boolean> {
    if (!this.g.program?.options?.fnac || !Array.isArray(products) || !products.length) return false

    const now = Date.now()
    const updatables = products.filter(p =>
      this.isFnacSeller(p.seller) && (!p.updatedTimestamp || (now - p.updatedTimestamp) > PRODUCT_TTL_MS)
    )
    if (!updatables.length) return false

    const settled = await Promise.allSettled(updatables.map(p => this.updateProduct(p)))
    return settled.some(r => r.status === 'fulfilled' && !!r.value)
  }

  /**
   * Extracts unique FNAC category IDs from a list of seller URL lines.
   * Returns an empty array if any URL is missing or does not match the expected pattern.
   * Exposed for testing.
   */
  private parseCategoryIds(lines: any[] = []): string[] {
    const ids = new Set<string>()
    for (const u of lines) {
      const url = u?.sellerUrl
      if (!url) return []
      const match = url.match(/(\D{1}\d{4,15})/)
      if (!match || !match[0]) return []
      ids.add(match[0])
    }
    return Array.from(ids)
  }

  /**
   * Scans a FNAC category, persists the returned products via a single batched
   * write, and stamps the category with `updatedTimestamp` only when every fetch
   * succeeded and produced products.
   *
   * Returns true when the category was scanned and its timestamp updated.
   */
  async scanCategory(category: any, force = false): Promise<boolean> {
    const now = Date.now()

    if (!this.g.program.options.fnac) return false
    if (!force && category.updatedTimestamp && (now - category.updatedTimestamp) <= CATEGORY_TTL_MS) return false

    if (!category.sellerUrlsLines || !category.sellerUrlsLines.length) return false

    const categoryIds = this.parseCategoryIds(category.sellerUrlsLines)
    if (!categoryIds.length) return false

    try {
      const settled = await Promise.allSettled(
        categoryIds.map(catId => this.fss.toPromise(this.api.get(`fnac/byCategory?categoryId=${catId}`)).then(unwrapApiResult))
      )

      // Abort if any fetch failed: do not stamp the category so the next access retries.
      if (settled.some(r => r.status === 'rejected')) return false

      const docs: any[] = []
      for (const r of settled) {
        const scan = (r as PromiseFulfilledResult<any>).value
        if (!scan || !Array.isArray(scan.products)) return false
        for (const prod of scan.products) {
          docs.push({
            ...prod,
            seller: category.seller || 'FNAC',
            vatRate: this.isOdisseiasSeller(category.seller) ? 0 : prod.vatRate,
            subcategory: category.name,
            category: category.parent,
            id: prod.sellerCode,
            apiFnac: true,
            updatedTimestamp: now,
            deliveryPeriod: DELIVERY_PERIOD_DAYS
          })
        }
      }

      if (!docs.length) return false

      await this.fss.setBatch('products', docs, { silent: true })
      await this.fss.update('categories', category.id, { updatedTimestamp: now }, { silent: true })
      return true
    } catch {
      return false
    }
  }
}
